// frontend/src/components/quick-capture/QuickCaptureCard.js
// Compact Quick Capture card for dashboard

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Link as MuiLink,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  CheckBox,
  Mic,
  Stop,
  OpenInFull
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useRecording } from '../../contexts/SimpleRecordingContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagProvider';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../api/axiosConfig';

const QuickCaptureCard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { get: getFlag } = useFeatureFlags();
  const voiceOff = getFlag('notes.composer.voiceOff');
  
  const {
    isRecording,
    recordingTime,
    currentTranscript,
    sessionId,
    toggleRecording,
    stopRecording: stopGlobalRecording
  } = useRecording();
  
  // Content state
  const [title, setTitle] = useState('');
  const [targetProject, setTargetProject] = useState('inbox');
  const [draftId] = useState(() => uuidv4());
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [error, setError] = useState('');
  const [savedNoteId, setSavedNoteId] = useState(null);
  
  // Real-time transcription option
  const [enableRealTimeTranscription, setEnableRealTimeTranscription] = useState(true);
  
  // Refs
  const titleInputRef = useRef(null);
  const lastTranscriptLengthRef = useRef(0);
  
  // TipTap editor for rich content
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        // Disable built-in extensions that we configure separately
        taskList: false,
        taskItem: false,
        link: false, // Disable to avoid duplicate with our custom Link config
        underline: false // Disable to avoid duplicate with our custom Underline config
      }),
      Underline,
      Link.configure({
        openOnClick: false
      }),
      TaskList,
      TaskItem.configure({
        nested: true
      }),
      Placeholder.configure({
        placeholder: 'Write your note...'
      })
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'quick-capture-editor'
      }
    }
  });

  // Auto-focus title on mount
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  // Real-time transcription insertion (optional)
  useEffect(() => {
    if (enableRealTimeTranscription && currentTranscript && editor && isRecording) {
      const newText = currentTranscript.substring(lastTranscriptLengthRef.current);
      if (newText) {
        editor.chain().focus().insertContent(newText).run();
        lastTranscriptLengthRef.current = currentTranscript.length;
      }
    }
  }, [currentTranscript, editor, enableRealTimeTranscription, isRecording]);


  // Handle voice recording toggle - redirect to Voices page
  const handleVoiceRecord = useCallback(async () => {
    // Navigate to voices page where recording should happen
    navigate('/voices');
    
    // Start recording after a brief delay to allow navigation
    setTimeout(async () => {
      await toggleRecording();
    }, 100);
  }, [navigate, toggleRecording]);

  // Check if content has changed
  const hasContent = () => {
    const editorContent = editor?.getHTML() || '';
    const isEmpty = editorContent === '<p></p>' || editorContent === '';
    return title.trim() !== '' || !isEmpty;
  };

  // Save note
  const handleSave = async () => {
    if (!hasContent() || isSaving) return;

    try {
      setIsSaving(true);
      setError('');
      
      // Finish voice session if recording and insert final transcript
      let audioFileId = null;
      let durationSec = null;
      
      if (sessionId) {
        try {
          // Insert the current transcript into editor before finishing session
          if (currentTranscript && editor) {
            editor.chain().focus().insertContent(currentTranscript).run();
          }
          
          const voiceResult = await stopGlobalRecording();
          if (voiceResult) {
            audioFileId = voiceResult.audioFileId;
            durationSec = voiceResult.durationSec;
          }
        } catch (voiceError) {
          console.error('Failed to finish voice session:', voiceError);
          // Continue saving note even if voice finalization fails
        }
      }
      
      const token = await currentUser.getIdToken();
      const editorContent = editor?.getHTML() || '';
      
      const payload = {
        title: title.trim() || '',
        content: '', // Will be generated from contentHtml
        contentHtml: editorContent,
        format: 'richtext',
        projectId: targetProject === 'inbox' ? null : targetProject,
        draftId: draftId,
        audioFileId,
        durationSec
      };

      const response = await axios.post('/api/notes', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Success - clear and show toast
      setSavedNoteId(response.data.note._id);
      setToastMessage('Saved ✓');
      setShowToast(true);
      
      // Clear content
      handleClear();
      
      // Refocus title
      if (titleInputRef.current) {
        titleInputRef.current.focus();
      }
      
      // Reset transcript tracking
      lastTranscriptLengthRef.current = 0;
      
      // Emit event for other components
      window.dispatchEvent(new CustomEvent('noteAdded'));
      
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Clear content
  const handleClear = () => {
    setTitle('');
    editor?.commands.clearContent();
    
    // Focus the title field after clear
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
    
    // Reset transcript tracking but don't stop recording
    lastTranscriptLengthRef.current = 0;
  };

  // Open full editor
  const handleOpenFullEditor = () => {
    const editorContent = editor?.getHTML() || '';
    // Navigate to full editor with current content
    navigate('/notes/new', { 
      state: { 
        initialTitle: title,
        initialContent: editorContent,
        initialFormat: 'richtext'
      }
    });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleClear();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
        e.preventDefault();
        handleVoiceRecord();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleClear, handleVoiceRecord]);

  const isActive = (format) => {
    if (!editor) return false;
    switch (format) {
      case 'bold': return editor.isActive('bold');
      case 'italic': return editor.isActive('italic');
      case 'underline': return editor.isActive('underline');
      case 'h1': return editor.isActive('heading', { level: 1 });
      case 'h2': return editor.isActive('heading', { level: 2 });
      case 'h3': return editor.isActive('heading', { level: 3 });
      case 'bulletList': return editor.isActive('bulletList');
      case 'taskList': return editor.isActive('taskList');
      default: return false;
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Card 
        sx={{ 
          maxHeight: 240,
          overflow: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header with title and target selection */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              ref={titleInputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="No title"
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '18px',
                  fontWeight: 500
                }
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Save to</InputLabel>
              <Select
                value={targetProject}
                onChange={(e) => setTargetProject(e.target.value)}
                label="Save to"
              >
                <MenuItem value="inbox">Inbox</MenuItem>
                {/* TODO: Add recent projects */}
              </Select>
            </FormControl>
          </Box>

          {/* Toolbar */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5, 
            mb: 1.5,
            pb: 1,
            borderBottom: '1px solid #f0f0f0'
          }}>
            {/* Format buttons */}
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
              color={isActive('h1') ? 'primary' : 'default'}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '10px' }}>H1</Typography>
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              color={isActive('h2') ? 'primary' : 'default'}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '10px' }}>H2</Typography>
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
              color={isActive('h3') ? 'primary' : 'default'}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '10px' }}>H3</Typography>
            </IconButton>
            
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleBold().run()}
              color={isActive('bold') ? 'primary' : 'default'}
            >
              <FormatBold sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              color={isActive('italic') ? 'primary' : 'default'}
            >
              <FormatItalic sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              color={isActive('underline') ? 'primary' : 'default'}
            >
              <FormatUnderlined sx={{ fontSize: 16 }} />
            </IconButton>
            
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              color={isActive('bulletList') ? 'primary' : 'default'}
            >
              <FormatListBulleted sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => editor?.chain().focus().toggleTaskList().run()}
              color={isActive('taskList') ? 'primary' : 'default'}
            >
              <CheckBox sx={{ fontSize: 16 }} />
            </IconButton>

            {/* Voice recording - hide when voiceOff flag is enabled */}
            {!voiceOff && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                {isRecording && (
                  <Typography variant="caption" sx={{ color: 'error.main', fontSize: '11px' }}>
                    🔴 {formatRecordingTime(recordingTime)}
                  </Typography>
                )}
                
                {/* Insert transcript button - only show if there's transcript text and real-time is disabled */}
                {currentTranscript && !enableRealTimeTranscription && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (currentTranscript && editor) {
                        editor.chain().focus().insertContent(currentTranscript).run();
                      }
                    }}
                    sx={{ 
                      fontSize: '10px', 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5
                    }}
                  >
                    Insert Text
                  </Button>
                )}
                
                <IconButton 
                  size="small" 
                  onClick={handleVoiceRecord}
                  color={isRecording ? 'error' : 'default'}
                  sx={{ 
                    backgroundColor: isRecording ? 'error.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: isRecording ? 'error.main' : 'action.hover'
                    }
                  }}
                >
                  {isRecording ? <Stop sx={{ fontSize: 16 }} /> : <Mic sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Editor */}
          <Box sx={{ minHeight: 60, maxHeight: 120, overflow: 'auto' }}>
            <EditorContent 
              editor={editor}
              style={{ outline: 'none' }}
            />
          </Box>

          {/* Real-time transcription toggle - hide when voiceOff flag is enabled */}
          {!voiceOff && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableRealTimeTranscription}
                    onChange={(e) => setEnableRealTimeTranscription(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Tooltip title="Automatically insert transcribed text as you speak">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Real-time transcription
                    </Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          
          {/* Voice note link - show when voiceOff flag is enabled */}
          {voiceOff && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <MuiLink
                component="button"
                variant="caption"
                onClick={() => navigate('/voices')}
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: '11px',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Record a voice note →
              </MuiLink>
            </Box>
          )}

          {/* Live transcript preview - only show if real-time is disabled */}
          {currentTranscript && !enableRealTimeTranscription && (
            <Box sx={{ 
              mt: 1, 
              p: 1, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 1,
              border: '1px dashed #dee2e6'
            }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                🎤 Live Transcript (click "Insert Text" or "Save" to use):
              </Typography>
              <Typography variant="body2" sx={{ 
                maxHeight: 40, 
                overflow: 'auto',
                fontSize: '13px',
                color: 'text.primary'
              }}>
                {currentTranscript}
              </Typography>
            </Box>
          )}

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 1, py: 0.5 }} onClose={() => setError('')}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {/* Actions */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 1.5 
          }}>
            <MuiLink 
              component="button"
              variant="body2"
              onClick={handleOpenFullEditor}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <OpenInFull sx={{ fontSize: 14 }} />
              Open full editor
            </MuiLink>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleClear}
                disabled={isSaving}
              >
                Clear
              </Button>
              <Button 
                variant="contained" 
                size="small"
                onClick={handleSave}
                disabled={!hasContent() || isSaving}
                startIcon={isSaving ? <CircularProgress size={14} /> : null}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Toast notification */}
      <Snackbar
        open={showToast}
        autoHideDuration={1500}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          onClose={() => setShowToast(false)}
          action={
            savedNoteId && (
              <Button 
                color="inherit" 
                size="small"
                onClick={() => navigate(`/notes/${savedNoteId}`)}
              >
                View note
              </Button>
            )
          }
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuickCaptureCard;